import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  SearchIcon,
  TagIcon,
  XIcon,
  Trash2Icon,
  PencilIcon,
  CheckIcon,
  CalendarIcon,
} from 'lucide-react';
import { Toast } from '../Toast';
import {
  useClientNotes,
  useInvalidateNotes,
  NoteRecord,
} from '../../hooks/useNotes';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientDetails } from '../../hooks/useClientDetails';
import { FileAttachments } from '../FileAttachments';
import { RichTextEditor } from '../RichTextEditor';
import 'react-quill/dist/quill.snow.css';

interface NotesTabProps {
  clientId: string;
}

type NoteFormState = {
  title: string;
  content: string;
  tags: string;
};

const emptyFormState: NoteFormState = {
  title: '',
  content: '',
  tags: '',
};

function splitTags(input: string) {
  return input
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Strip HTML tags and decode HTML entities for plain text display
function stripHtml(html: string): string {
  // Create a temporary div to leverage browser's HTML parsing
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

export function NotesTab({ clientId }: NotesTabProps) {
  const navigate = useNavigate();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const notesQuery = useClientNotes(clientId);
  const clientQuery = useClientDetails(clientId);
  const invalidateNotes = useInvalidateNotes(clientId);

  const [showAddNote, setShowAddNote] = useState(false);
  const [noteForm, setNoteForm] = useState<NoteFormState>(emptyFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingForm, setEditingForm] =
    useState<NoteFormState>(emptyFormState);
  const [editingError, setEditingError] = useState<string | null>(null);

  const notes = notesQuery.data ?? [];
  const filteredNotes = useMemo(() => {
    if (!searchQuery.trim()) return notes;
    return notes.filter((note) => {
      const query = searchQuery.toLowerCase();
      // Remove # from search query if present for tag matching
      const normalizedQuery = query.startsWith('#') ? query.slice(1) : query;

      return (
        note.title.toLowerCase().includes(query) ||
        note.body.toLowerCase().includes(query) ||
        note.tags.some(tag => tag.toLowerCase().includes(normalizedQuery))
      );
    });
  }, [notes, searchQuery]);

  const createNoteMutation = useMutation({
    mutationFn: async (payload: NoteFormState) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      if (!payload.title.trim() || !payload.content.trim()) {
        throw new Error('Title and content are required');
      }
      return apiRequest<NoteRecord>(
        `/api/clients/${clientId}/notes`,
        {
          method: 'POST',
          body: JSON.stringify({
            title: payload.title.trim(),
            body: payload.content.trim(),
            tags: splitTags(payload.tags),
          }),
        },
        session.access_token,
      );
    },
    onSuccess: (data) => {
      setShowAddNote(false);
      setNoteForm(emptyFormState);
      setFormError(null);
      setToastMessage('Note saved');
      invalidateNotes();
    },
    onError: (error) => {
      setFormError(error instanceof Error ? error.message : 'Failed to save note');
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({
      noteId,
      payload,
    }: {
      noteId: string;
      payload: NoteFormState;
    }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      return apiRequest<NoteRecord>(
        `/api/clients/${clientId}/notes/${noteId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            title: payload.title.trim(),
            body: payload.content.trim(),
            tags: splitTags(payload.tags),
          }),
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      setEditingNoteId(null);
      setEditingForm(emptyFormState);
      setEditingError(null);
      setToastMessage('Note updated');
      invalidateNotes();
    },
    onError: (error) => {
      setEditingError(
        error instanceof Error ? error.message : 'Failed to update note',
      );
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async ({
      noteId,
    }: {
      noteId: string;
      noteTitle: string;
    }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      return apiRequest(
        `/api/clients/${clientId}/notes/${noteId}`,
        { method: 'DELETE' },
        session.access_token,
      );
    },
    onSuccess: (_, variables) => {
      setToastMessage('Note deleted');
      invalidateNotes();
      if (editingNoteId === variables.noteId) {
        setEditingNoteId(null);
        setEditingForm(emptyFormState);
      }
    },
  });

  const handleSaveNote = async () => {
    await createNoteMutation.mutateAsync(noteForm);
  };

  const handleStartEditing = (note: NoteRecord) => {
    setEditingNoteId(note.id);
    setEditingForm({
      title: note.title,
      content: note.body,
      tags: note.tags.join(', '),
    });
    setEditingError(null);
  };

  const handleUpdateNote = async () => {
    if (!editingNoteId) return;
    await updateNoteMutation.mutateAsync({
      noteId: editingNoteId,
      payload: editingForm,
    });
  };

  const handleDeleteNote = async (note: NoteRecord) => {
    await deleteNoteMutation.mutateAsync({
      noteId: note.id,
      noteTitle: note.title,
    });
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-semibold text-base text-[#212121]">
            Interaction Notes
          </h3>
          <span className="text-xs text-gray-500">
            ({filteredNotes.length})
          </span>
        </div>
        <div className="relative mb-3">
          <SearchIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search notes..."
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
          />
        </div>

        {notesQuery.isLoading ? (
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 text-center text-gray-500">
            Loading notes...
          </div>
        ) : notesQuery.isError ? (
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-red-200 text-center text-red-500">
            Unable to load notes right now.
          </div>
        ) : filteredNotes.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TagIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-[#212121] mb-2">
              No notes yet
            </h3>
            <p className="text-gray-600 mb-4">
              Capture learnings from calls, follow-ups, objections, and
              preferences here.
            </p>
            <button
              onClick={() => setShowAddNote(true)}
              className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg font-medium"
            >
              Add first note
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotes.map((note) => {
              const isEditing = editingNoteId === note.id;
              return (
                <div
                  key={note.id}
                  className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => !isEditing && navigate(`/client/${clientId}/note/${note.id}`)}
                    >
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingForm.title}
                          onChange={(e) =>
                            setEditingForm((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none text-sm font-semibold mb-2"
                          placeholder="Note title"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <h4 className="font-semibold text-sm text-[#212121] hover:text-[#5B8E87] transition-colors">
                          {note.title}
                        </h4>
                      )}
                      <p className="text-xs text-gray-500">
                        {formatDate(note.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleUpdateNote}
                            className="p-2 bg-[#5B8E87]/10 text-[#5B8E87] rounded-lg"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingNoteId(null);
                              setEditingForm(emptyFormState);
                            }}
                            className="p-2 bg-gray-100 text-gray-600 rounded-lg"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEditing(note);
                            }}
                            className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNote(note);
                            }}
                            className="p-2 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <Trash2Icon className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {isEditing ? (
                    <>
                      {editingError && (
                        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-2">
                          {editingError}
                        </div>
                      )}
                      <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                        <RichTextEditor
                          value={editingForm.content}
                          onChange={(value) =>
                            setEditingForm((prev) => ({
                              ...prev,
                              content: value,
                            }))
                          }
                          placeholder="Note content..."
                          minHeight="150px"
                        />
                      </div>
                      <input
                        type="text"
                        value={editingForm.tags}
                        onChange={(e) =>
                          setEditingForm((prev) => ({
                            ...prev,
                            tags: e.target.value,
                          }))
                        }
                        className="w-full mt-2 px-3 py-2 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none text-sm"
                        placeholder="Tags (comma separated)"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </>
                  ) : (
                    <>
                      <p
                        className="text-sm text-gray-600 mt-2 leading-relaxed line-clamp-2"
                        onClick={() => navigate(`/client/${clientId}/note/${note.id}`)}
                      >
                        {stripHtml(note.body)}
                      </p>
                      {note.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {note.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
                            >
                              #{tag}
                            </span>
                          ))}
                          {note.tags.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{note.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowAddNote(true)}
        className="fixed bottom-24 right-6 bg-[#C9A870] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#B89660] transition-all active:scale-95 z-20"
      >
        <PlusIcon className="w-6 h-6" />
      </button>

      {showAddNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#212121]">
                Add Note
              </h2>
              <button
                onClick={() => {
                  setShowAddNote(false);
                  setNoteForm(emptyFormState);
                  setFormError(null);
                }}
                className="text-gray-500"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {formError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={noteForm.title}
                  onChange={(e) =>
                    setNoteForm((prev) => ({ ...prev, title: e.target.value }))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                  placeholder="e.g., Weekly call recap"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Details
                </label>
                <RichTextEditor
                  value={noteForm.content}
                  onChange={(value) =>
                    setNoteForm((prev) => ({ ...prev, content: value }))
                  }
                  placeholder="Summarize the discussion, commitments, blockers..."
                  minHeight="200px"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Tags
                </label>
                <input
                  type="text"
                  value={noteForm.tags}
                  onChange={(e) =>
                    setNoteForm((prev) => ({ ...prev, tags: e.target.value }))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                  placeholder="check-in, plan update"
                />
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowAddNote(false);
                  setNoteForm(emptyFormState);
                  setFormError(null);
                }}
                className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNote}
                disabled={createNoteMutation.isPending}
                className={`flex-1 font-semibold py-3 rounded-xl ${
                  createNoteMutation.isPending
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-[#5B8E87] text-white'
                }`}
              >
                {createNoteMutation.isPending ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
